import { Button } from "../ui/button";
import { Trash2, FileText, Eye, ExternalLink, CheckSquare, Square } from "lucide-react";

interface MediaAsset {
  key: string;
  type: "image" | "raw";
  url: string;
  thumbnailUrl: string | null;
  size: number;
}

function formatSize(bytes: number) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

interface MediaAssetCardProps {
  asset: MediaAsset;
  isSelected: boolean;
  onToggle: (key: string) => void;
  onPreview: (asset: MediaAsset) => void;
  onDelete: (key: string) => void;
}

export function MediaAssetCard({ asset, isSelected, onToggle, onPreview, onDelete }: MediaAssetCardProps) {
  return (
    <div
      className={`relative group border rounded-xl overflow-hidden bg-card/50 hover:bg-card hover:shadow-md transition-all duration-200 ${isSelected ? "border-brand-primary ring-2 ring-brand-primary/20" : "border-border/60"
        }`}
    >
      <button
        type="button"
        onClick={() => onToggle(asset.key)}
        className={`absolute top-2 left-2 z-10 p-1.5 rounded-lg border backdrop-blur-md transition-all ${isSelected
            ? "bg-brand-primary border-brand-primary text-white"
            : "bg-black/30 border-white/20 text-white opacity-0 group-hover:opacity-100"
          }`}
      >
        {isSelected ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
      </button>

      <div
        className="aspect-square bg-muted/40 relative cursor-pointer overflow-hidden flex items-center justify-center border-b border-border/40"
        onClick={() => onPreview(asset)}
      >
        {asset.type === "image" ? (
          <img
            src={asset.thumbnailUrl || asset.url}
            alt={asset.key}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-muted-foreground gap-2 p-4">
            <FileText className="h-10 w-10 text-brand-secondary/65" />
            <span className="text-[10px] font-bold uppercase tracking-wider bg-muted px-2 py-0.5 rounded border border-border/40">
              {asset.key.split(".").pop()}
            </span>
          </div>
        )}

        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <Button
            size="icon"
            variant="ghost"
            type="button"
            className="h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 text-white border-0"
            onClick={(e) => {
              e.stopPropagation();
              onPreview(asset);
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <a
            href={asset.url}
            target="_blank"
            rel="noopener noreferrer"
            className="h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-4 w-4" />
          </a>
          <Button
            size="icon"
            variant="ghost"
            type="button"
            className="h-8 w-8 rounded-lg bg-destructive/80 hover:bg-destructive text-white border-0"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(asset.key);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="p-3 space-y-1">
        <p className="text-xs font-semibold truncate text-foreground leading-normal" title={asset.key}>
          {asset.key.split("-").pop()}
        </p>
        <div className="flex justify-between items-center text-[10px] text-muted-foreground">
          <span>{formatSize(asset.size)}</span>
          <span className="font-mono text-[8px] bg-muted px-1 rounded uppercase">
            {asset.type}
          </span>
        </div>
      </div>
    </div>
  );
}

export default MediaAssetCard;
